import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Upload, Sparkles } from 'lucide-react';
import Sidebar from './Sidebar';

/**
 * Premium "Coming Soon" placeholder used for modules that are wired in the
 * navigation but whose data hasn't been imported yet (Work Orders, Contracts,
 * Supply, Readiness Plan, Facility Engineers, Supervisors, Site Visits, Map).
 * Keeps the same aesthetic language as the redesigned dashboard.
 */
export default function ComingSoonPage({
  title,
  arabicTitle,
  eyebrow = 'وحدة قادمة',
  description,
  icon: Icon = Sparkles,
  accent = '#6FA8FF',
  testId = 'coming-soon-page',
}) {
  const nav = useNavigate();

  return (
    <div dir="rtl" className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-10" data-testid={testId}>
        {/* Breadcrumb */}
        <div className="text-slate-400 text-[11px] font-medium mb-8 flex items-center gap-2">
          <button onClick={() => nav('/')} className="hover:text-slate-200 transition-colors">لوحة القيادة</button>
          <span className="text-slate-600">›</span>
          <span className="text-slate-300">{arabicTitle}</span>
        </div>

        {/* Hero card */}
        <div className="glass fade-in relative overflow-hidden max-w-[900px] mx-auto p-10 md:p-14">
          {/* Ambient accent */}
          <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-40 blur-3xl"
               style={{ background: `radial-gradient(circle, ${accent}55 0%, transparent 60%)` }} />
          <div className="pointer-events-none absolute -bottom-24 -right-24 w-80 h-80 rounded-full opacity-30 blur-3xl"
               style={{ background: 'radial-gradient(circle, rgba(180,156,255,0.35) 0%, transparent 60%)' }} />

          <div className="relative flex flex-col items-center text-center gap-6">
            <div className="section-eyebrow" style={{ color: accent }}>{eyebrow}</div>

            <div
              className="w-20 h-20 rounded-3xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${accent}22 0%, rgba(180,156,255,0.14) 100%)`,
                border: `1px solid ${accent}30`,
                boxShadow: `0 12px 40px -14px ${accent}55, 0 1px 0 rgba(255,255,255,0.06) inset`,
              }}
            >
              <Icon size={30} strokeWidth={1.6} style={{ color: accent }} />
            </div>

            <div>
              <h1 className="text-white text-[30px] md:text-[36px] font-extrabold tracking-tight leading-tight">{arabicTitle}</h1>
              <div className="text-slate-400 text-[13px] mt-1.5">{title}</div>
            </div>

            <p className="text-slate-300 text-[14px] leading-7 max-w-[560px]">
              {description || 'ستُفعّل هذه الوحدة تلقائياً بمجرد استيراد ملف Excel الخاص بها. تصميم واجهة الوحدة جاهز — البيانات فقط بانتظار الرفع.'}
            </p>

            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => nav('/imports')}
                className="btn btn-primary"
                data-testid={`${testId}-import`}
              >
                <Upload size={14} strokeWidth={1.8} />
                <span>الذهاب إلى مركز الاستيراد</span>
              </button>
              <button
                onClick={() => nav('/')}
                className="btn btn-ghost"
                data-testid={`${testId}-back`}
              >
                <ArrowRight size={14} strokeWidth={1.8} />
                <span>العودة إلى الرئيسية</span>
              </button>
            </div>

            {/* Meta strip */}
            <div className="w-full pt-8 mt-4 border-t hair grid grid-cols-3 gap-2 text-center">
              <MetaCell label="الحالة" value="بانتظار البيانات" tone={accent} />
              <MetaCell label="المصدر" value="ملف Excel" />
              <MetaCell label="التصميم" value="جاهز" tone="#86EFAC" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MetaCell({ label, value, tone }) {
  return (
    <div className="py-1">
      <div className="text-[10.5px] font-semibold tracking-wider uppercase text-slate-500">{label}</div>
      <div className="text-[13px] font-bold mt-1" style={{ color: tone || '#EDEFF5' }}>{value}</div>
    </div>
  );
}
